import axios from 'axios';
import { ApiResponse } from '../types';

// Configure base URL - update this to match your Flask backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 240000, // 4 minutes timeout to match your Streamlit app
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
apiClient.interceptors.request.use((config) => {
  console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
  return config;
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export interface ChatRequest {
  prompt: string;
  session_id?: string | null;
}

export interface CancelRequest {
  session_id: string;
}

export interface SaveReportRequest {
  topic: string;
  query: string;
  report_data: any;
}

export interface DemoReportResponse {
  status: string;
  report_data: any;
  message: string;
}

export const apiService = {
  /**
   * Send a chat message to the backend orchestrator
   */
  async sendMessage(request: ChatRequest): Promise<ApiResponse> {
    const response = await apiClient.post<ApiResponse>('/api/chat', request);
    return response.data;
  },

  /**
   * Health check endpoint (optional - you may want to add this to your Flask backend)
   */
  async healthCheck(): Promise<{ status: string }> {
    const response = await apiClient.get('/health');
    return response.data;
  },

  /**
   * Cancel an ongoing analysis session
   */
  async cancelAnalysis(request: CancelRequest): Promise<{ status: string }> {
    const response = await apiClient.post('/api/cancel', request, {
      timeout: 5000, // 5 second timeout for cancel requests
    });
    return response.data;
  },

  /**
   * Save current analysis report for demo mode
   */
  async saveReport(request: SaveReportRequest): Promise<{ status: string; filename: string; message: string }> {
    const response = await apiClient.post('/api/save-report', request);
    return response.data;
  },

  /**
   * Load demo report for instant display
   */
  async loadDemoReport(topic: string): Promise<DemoReportResponse> {
    const response = await apiClient.get(`/api/load-demo-report/${topic}`);
    return response.data;
  },
};

export default apiService;